/**
 * Notification Event Type Registry
 *
 * Maps event type strings to their expected payload interfaces.
 * This is extensible: new event types can be added by module augmentation.
 *
 * Usage:
 *   // In your domain module:
 *   declare module '@zag/notifications/common' {
 *     interface NotificationEventMap {
 *       plan_published: PlanPublishedPayload;
 *       shift_assigned: ShiftAssignedPayload;
 *     }
 *   }
 */

interface CalendarSlotHandoffPayload {
  slotId: string;
  calendarId: string;
  dateFrom: string;
  startTime?: string;
  endTime?: string;
  title?: string;
  actorName?: string;
}

/**
 * Extensible event type → payload map.
 * Domain modules extend this via declaration merging.
 */
export interface NotificationEventMap {
  /** Fallback for legacy/migrated notifications. */
  legacy_import: { title: string; message: string; type: string };

  /**
   * Fired once when a new user account is created —
   * either via email/password registration or the first social login.
   */
  user_account_created: { userId: string; email: string };

  /**
   * Fired once per version per tenant when the API starts with a new
   * package.json version. Sends an in-app notification to all users.
   */
  app_version_released: { version: string };

  'bulk-edit-finished': {
    jobId: string;
    collectionKey: string;
    resourceKey: string;
    totals: { succeeded: number; failed: number; skipped: number; cancelled: number };
  };

  /**
   * Employee changed their personal time override for an assigned shift unit.
   * Sent to planners responsible for the team.
   */
  shift_time_override_by_employee: {
    planInstanceId: string;
    shiftUnitId: string;
    shiftName: string;
    date: string;
    employeeId: string;
    employeeName: string;
    originalStart: string;
    originalEnd: string;
    newStart: string;
    newEnd: string;
  };

  /**
   * Planner reset an employee's personal shift override (time and/or location).
   * Sent to the affected employee.
   */
  shift_override_reset_by_planner: {
    planInstanceId: string;
    shiftUnitId: string;
    shiftName: string;
    date: string;
    plannerId: string;
    plannerName: string;
  };

  /** Sent to the calendar owner's linked user when another user creates a slot in their calendar. */
  calendar_slot_created_for_you: {
    slotId: string;
    calendarId: string;
    dateFrom: string;
    startTime?: string;
    endTime?: string;
    title?: string;
    createdByName?: string;
  };

  /** Sent to the owner's linked user of the target calendar when another user hands a slot over to it. */
  calendar_slot_handed_to_you: CalendarSlotHandoffPayload;

  /** Sent to the owner's linked user of the source calendar when another user takes a slot away from it. */
  calendar_slot_taken_from_you: CalendarSlotHandoffPayload;

  /**
   * Fired whenever a new calendar slot is created via the calendar API.
   * No natural person recipient — consumed only by webhook subscribers,
   * dispatched via `NotificationDispatchService.emitSystemEvent()`. `onlineMeeting` is the verified Teams join link at dispatch, else null.
   */
  calendar_slot_created: {
    calendarId: string;
    slotId: string;
    dateFrom: string;
    dateTo: string | null;
    recurrenceRule: string | null;
    data: Record<string, unknown> | null;
    onlineMeeting: { provider: 'teams'; joinUrl: string } | null;
  };

  /**
   * Fired whenever an existing calendar slot is updated via the calendar API.
   * Same dispatch mechanism and `onlineMeeting` semantics as `calendar_slot_created`.
   */
  calendar_slot_updated: {
    calendarId: string;
    slotId: string;
    dateFrom: string;
    dateTo: string | null;
    recurrenceRule: string | null;
    data: Record<string, unknown> | null;
    onlineMeeting: { provider: 'teams'; joinUrl: string } | null;
  };

  /**
   * Fired whenever a calendar slot is deleted via the calendar API.
   * `data` reflects the slot's last known state before deletion. `onlineMeeting` is normally null: the join link detaches on delete.
   */
  calendar_slot_deleted: {
    calendarId: string;
    slotId: string;
    dateFrom: string;
    dateTo: string | null;
    recurrenceRule: string | null;
    data: Record<string, unknown> | null;
    onlineMeeting: { provider: 'teams'; joinUrl: string } | null;
  };

  /**
   * Employee submitted an absence approval request.
   * Sent to the approvers responsible for the person.
   */
  'timetracking.absence.approvalRequested': {
    requestId: string;
    personId: string;
    personName: string;
    absenceTypeName: string;
    fromDate: string;
    toDate: string;
    dayCount: number;
  };

  /**
   * An approver approved or rejected an absence request.
   * Sent to the requester.
   */
  'timetracking.absence.decisionMade': {
    requestId: string;
    decision: 'approved' | 'rejected';
    absenceTypeName: string;
    fromDate: string;
    toDate: string;
    decidedBy: string;
    comment?: string;
  };

  /**
   * Booking inside a blocked time window raised an exception request.
   * Sent to the approvers responsible for the person.
   */
  'timetracking.blocked_window_exception.approvalRequested': {
    requestId: string;
    personId: string;
    personName: string;
    bookingId: string;
    date: string;
    windowKeys: string[];
  };

  /**
   * An approver approved or rejected a blocked-window exception request.
   * Sent to the requester.
   */
  'timetracking.blocked_window_exception.decisionMade': {
    requestId: string;
    decision: 'approved' | 'rejected';
    bookingId: string;
    date: string;
    comment: string | null;
  };

  /**
   * Requester withdrew a still-pending absence request.
   * Sent to the approvers who were asked to decide it.
   */
  'timetracking.absence.withdrawn': {
    requestId: string;
    personName: string;
    absenceTypeName: string;
    fromDate: string;
    toDate: string;
  };

  /**
   * Employee requested a personal shift time override that needs approval.
   * Sent to the approvers responsible for the team.
   */
  'planning.shift-override.approvalRequested': {
    requestId: string;
    planInstanceId: string;
    date: string;
    shiftUnitName?: string;
    employeeName?: string;
    requestedStartTime: string;
    requestedEndTime: string;
  };

  /**
   * An approver approved or rejected a shift override request.
   * Sent to the requester.
   */
  'planning.shift-override.decisionMade': {
    requestId: string;
    planInstanceId: string;
    date: string;
    shiftUnitName?: string;
    employeeName?: string;
    requestedStartTime: string;
    requestedEndTime: string;
    decision: 'approved' | 'rejected';
    comment?: string;
  };

  /**
   * Requester withdrew a still-pending shift override request.
   * Sent to the approvers who were asked to decide it.
   */
  'planning.shift-override.withdrawn': {
    requestId: string;
    planInstanceId: string;
    date: string;
    shiftUnitName?: string;
    employeeName?: string;
    requestedStartTime: string;
    requestedEndTime: string;
  };
}

/**
 * All registered event type strings.
 */
export type NotificationEventType = keyof NotificationEventMap;
